import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { collection, getDocs, query, where, updateDoc, doc, addDoc, setDoc } from 'firebase/firestore';
import { trackAuth, setUserData, trackSession } from '../utils/analytics';
import { logUserActivity, ACTIVITY_TYPES } from '../utils/userActivityLogger';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, classSelection = 'Empty', firstName = '', lastName = '') {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Track signup event
    trackAuth.signup('email');
    
    // Create user document in Firestore with class selection and names
    await createUserDocument(userCredential.user, classSelection, firstName, lastName);
    
    // Set user analytics data with enhanced tracking
    setUserData(userCredential.user.uid, {
      user_type: 'new',
      class_selection: classSelection,
      has_name: !!(firstName && lastName),
      signup_date: new Date().toISOString().split('T')[0]
    });
    
    // Assign unassigned events to new user
    await assignUnassignedEventsToUser(userCredential.user.uid);
    
    // Add preloaded events if Class 1 or Arbor School is selected
    if (classSelection === 'Class 1' || classSelection === 'Arbor School' || classSelection === 'Arbor School - 1ST (Grade One)') {
      await addPreloadedEventsForClass1(userCredential.user.uid);
    }
    
    return userCredential;
  }

  // Create user document in Firestore
  async function createUserDocument(user, classSelection = 'Empty', firstName = '', lastName = '') {
    try {
      // Set classID based on classSelection - same for all users in that class
      let classID = classSelection; // Use the classSelection as the classID
      if (classSelection === 'Arbor School' || classSelection === 'Arbor School - 1ST (Grade One)') {
        classID = 'ArborSchool-1ST(Grade One)';
      }
      
      // Create display name from first and last name
      const displayName = firstName && lastName ? `${firstName} ${lastName}` : user.displayName || null;
      
      // Set default permissions for new users - full permissions by default
      const defaultPermissions = {
        canViewAllEvents: true,  // Can see all family events by default
        canEditEvents: true      // Can create and edit events by default
      };
      
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        firstName: firstName || null,
        lastName: lastName || null,
        photoURL: user.photoURL || null,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        isActive: true,
        classSelection: classSelection,
        classID: classID,
        permissions: defaultPermissions
      });
      console.log('User document created successfully with classID:', classID, 'and permissions:', defaultPermissions);
    } catch (error) {
      console.error('Error creating user document:', error);
      // Don't throw error as this shouldn't prevent signup
    }
  }

  // Update user's last login time
  async function updateUserLoginTime(user) {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        lastLoginAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating user login time:', error);
      // Don't throw error as this is not critical
    }
  }

  async function login(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Track login event
    trackAuth.login('email');
    
    // Log detailed user activity
    await logUserActivity(userCredential.user.uid, ACTIVITY_TYPES.LOGIN, {
      method: 'email',
      email: email
    });
    
    // Check if user document exists and create/update it
    try {
      const userDocSnapshot = await getDocs(query(collection(db, 'users'), where('uid', '==', userCredential.user.uid)));
      
      if (userDocSnapshot.empty) {
        // User document doesn't exist, create it
        await createUserDocument(userCredential.user);
      } else {
        // User document exists, check if they have permissions and add defaults if missing
        const userData = userDocSnapshot.docs[0].data();
        if (!userData.permissions) {
          console.log('Adding default permissions to existing user');
          const defaultPermissions = {
            canViewAllEvents: true,
            canEditEvents: true
          };
          await updateDoc(userDocSnapshot.docs[0].ref, {
            permissions: defaultPermissions,
            lastLoginAt: new Date().toISOString()
          });
        } else {
          // User document exists with permissions, just update login time
          await updateUserLoginTime(userCredential.user);
        }
      }
    } catch (error) {
      console.error('Error handling user document during login:', error);
      // Don't throw error as this shouldn't prevent login
    }
    
    return userCredential;
  }

  function logout() {
    // Track logout event
    trackAuth.logout();
    trackSession.end();
    return signOut(auth);
  }

  // Function to add preloaded events for Class 1 and Arbor School
  async function addPreloadedEventsForClass1(userId) {
    try {
      // Check if events already exist in Arbor1 collection
      const arbor1EventsRef = collection(db, 'Arbor1');
      const existingSnapshot = await getDocs(arbor1EventsRef);
      
      if (existingSnapshot.size > 0) {
        console.log(`Arbor1 collection already has ${existingSnapshot.size} events. Skipping preload.`);
        return;
      }
      
      const classID = 'ArborSchool-1ST(Grade One)';
      const preloadedEvents = [
        {
          eventName: 'Year 1 Show Production – An Exciting Summer Adventure',
          startTime: '08:30 AM',
          endTime: '09:30 AM',
          date: new Date(2025, 5, 19), // June 19, 2025 (month is 0-indexed)
          day: 19,
          month: 6,
          year: 2025,
          hour: 8,
          minute: 30,
          category: 'school',
          description: 'Please join us for a memorable performance by the children.',
          location: 'School Auditorium',
          hasTime: true,
          isAllDay: false,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Year 1 Show Production'
        },
        {
          eventName: 'Inspired by Nature – Whole School Event',
          startTime: '10:00 AM',
          endTime: '12:00 PM',
          date: new Date(2025, 5, 20), // June 20, 2025 (month is 0-indexed)
          day: 20,
          month: 6,
          year: 2025,
          hour: 10,
          minute: 0,
          category: 'school',
          description: 'Parents and families are invited to attend this nature-inspired celebration. Parents will be able to collect their child from class and explore the exhibits together, or remain with the class as they explore together.',
          location: '',
          hasTime: true,
          isAllDay: false,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Inspired by Nature Event'
        },
        {
          eventName: 'Instructional Writing – Home Learning Task',
          date: new Date(2025, 4, 26), // May 26, 2025 (month is 0-indexed)
          day: 26,
          month: 5,
          year: 2025,
          category: 'school',
          description: 'Children present demonstration to their classmates - please practice at home too!',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          participants: []
        },
        {
          eventName: 'Year 1 Trip to OliOli!',
          date: new Date(2025, 5, 23), // June 23, 2025 (month is 0-indexed)
          day: 23,
          month: 6,
          year: 2025,
          category: 'school',
          description: 'Related to Sustainable Toymaker project. Please complete the Zenda payment - where full details regarding lunch, clothing and requirement are also available.',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Year 1 Trip to OliOli',
          participants: []
        },
        {
          eventName: 'STEAM Fortnight',
          date: new Date(2025, 5, 2), // June 2, 2025 (month is 0-indexed)
          day: 2,
          month: 6,
          year: 2025,
          hour: 0,
          minute: 0,
          category: 'school',
          description: 'Volunteer opportunities available - please see the email for more information.',
          location: 'Arbor School',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'STEAM Fortnight'
        },
        {
          eventName: 'Parent Talk - BSO Inspection Results',
          startTime: '08:00 AM',
          endTime: '09:00 AM',
          date: new Date(2025, 4, 28), // May 28, 2025 (month is 0-indexed)
          day: 28,
          month: 5,
          year: 2025,
          hour: 8,
          minute: 0,
          endHour: 9,
          endMinute: 0,
          category: 'school',
          description: 'Principal shares BSO report feedback',
          location: 'Marketplace, 2nd floor',
          hasTime: true,
          isAllDay: false,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          participants: []
        },
        {
          eventName: 'Arbor\'s Got Talent Finals',
          date: new Date(2025, 5, 25), // June 25, 2025 (month is 0-indexed)
          day: 25,
          month: 6,
          year: 2025,
          hour: 0,
          minute: 0,
          category: 'school',
          description: 'Competition finals',
          location: '',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Arbor\'s Got Talent Finals'
        },
        {
          eventName: 'Project Landing – \'The Sustainable Toymaker\'',
          startTime: '08:15 AM',
          endTime: '09:15 AM',
          date: new Date(2025, 5, 4), // June 4, 2025 (month is 0-indexed)
          day: 4,
          month: 6,
          year: 2025,
          hour: 8,
          minute: 15,
          category: 'school',
          description: 'Children will showcase their finished sustainable toys and share the creative process behind them. Children will also display their writing and ideas developed throughout the project.',
          location: 'Ground Floor Canteen',
          hasTime: true,
          isAllDay: false,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Project Landing'
        },
        {
          eventName: 'Arbor\'s Got Talent Phase 1',
          date: new Date(2025, 5, 13), // June 13, 2025 (month is 0-indexed)
          day: 13,
          month: 6,
          year: 2025,
          hour: 0,
          minute: 0,
          category: 'school',
          description: 'Competition begins - 4 students in year one will move onto the finals after this phase.',
          location: '',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Arbor\'s Got Talent Phase 1'
        },
        {
          eventName: 'Father\'s Day Song – Picture Request',
          date: new Date(2025, 4, 30), // May 30, 2025 (month is 0-indexed)
          day: 30,
          month: 5,
          year: 2025,
          category: 'school',
          description: 'Special Father\'s Day celebration prep - please send in photos on or before this date.',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          participants: []
        },
        {
          eventName: 'End of Term - Last Day of Academic Year',
          date: new Date(2025, 5, 25), // June 25, 2025 (month is 0-indexed)
          day: 25,
          month: 6,
          year: 2025,
          category: 'school',
          description: 'Year end party this day',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'End of Term',
          participants: []
        },
        {
          eventName: 'Deadline for Transport Registration',
          date: new Date(2025, 6, 3), // July 3, 2025 (month is 0-indexed)
          day: 3,
          month: 7,
          year: 2025,
          category: 'school',
          description: 'See link: https://forms.office.com/Pages/ResponsePage.aspx?id=5pNODFCb6Uu5F2AYaoSaPs51j7tbIQ9CqI0_O992PVpUOEhSSExLSDJQTE9DM0pIMlJIRjBKU0Y3QS4u - Fill out one form per child',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Transport Registration Deadline',
          participants: []
        }
      ];

      const addPromises = preloadedEvents.map(event => addDoc(arbor1EventsRef, event));
      
      await Promise.all(addPromises);
      console.log(`Added ${preloadedEvents.length} shared events to Arbor1 collection`);
    } catch (error) {
      console.error('Error adding preloaded events:', error);
    }
  }

  // Helper function to get the next occurrence of a weekday
  function getNextWeekday(dayNumber) {
    const today = new Date();
    const todayDay = today.getDay();
    const daysUntilNext = (dayNumber - todayDay + 7) % 7 || 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilNext);
    return nextDate.toISOString().split('T')[0];
  }

  // Function to assign existing events without userId to a user
  async function assignUnassignedEventsToUser(userId) {
    try {
      // Query for all events without userId in pendingEvents
      const pendingEventsRef = collection(db, 'pendingEvents');
      const pendingQuery = query(pendingEventsRef, where('userId', '==', null));
      const pendingSnapshot = await getDocs(pendingQuery);

      // Query for all events without userId in acceptedEvents
      const acceptedEventsRef = collection(db, 'acceptedEvents');
      const acceptedQuery = query(acceptedEventsRef, where('userId', '==', null));
      const acceptedSnapshot = await getDocs(acceptedQuery);

      // Update all unassigned events to belong to this user
      const updatePromises = [];
      
      pendingSnapshot.forEach((docSnapshot) => {
        updatePromises.push(
          updateDoc(doc(db, 'pendingEvents', docSnapshot.id), { userId })
        );
      });

      acceptedSnapshot.forEach((docSnapshot) => {
        updatePromises.push(
          updateDoc(doc(db, 'acceptedEvents', docSnapshot.id), { userId })
        );
      });

      await Promise.all(updatePromises);
      console.log(`Assigned ${updatePromises.length} unassigned events to user ${userId}`);
    } catch (error) {
      console.error('Error assigning unassigned events:', error);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);
      
      if (user) {
        // Track session start when user is authenticated
        trackSession.start();
        
        // Set user data for analytics
        try {
          const userDoc = await getDocs(query(collection(db, 'users'), where('uid', '==', user.uid)));
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            setUserData(user.uid, {
              user_type: 'returning',
              class_selection: userData.classSelection || 'unknown',
              has_permissions: !!userData.permissions
            });
          }
        } catch (error) {
          console.error('Error setting user analytics data:', error);
        }
      }
    });

    return unsubscribe;
  }, []);

  // Function to add preloaded accepted events to Arbor1 collection
  async function addPreloadedEventsToArbor1(classID = 'ArborSchool-1ST(Grade One)') {
    try {
      // This function can be used to add accepted events to Arbor1
      // Currently handled by addPreloadedEventsForClass1
      console.log('Preloaded accepted events are handled by addPreloadedEventsForClass1');
    } catch (error) {
      console.error('Error adding preloaded events:', error);
    }
  }

  // Function to add preloaded pending events to Arbor1PendingEvents collection
  async function addPreloadedPendingEventsToArbor1(classID = 'ArborSchool-1ST(Grade One)') {
    try {
      const preloadedPendingEvents = [
        {
          eventName: 'Sample',
          date: new Date(2025, 4, 25), // May 25, 2025 (month is 0-indexed)
          day: 25,
          month: 5,
          year: 2025,
          hour: 16,
          minute: 0,
          startTime: '04:00 PM',
          endTime: '05:00 PM',
          category: 'life',
          description: 'A sample event taking place.',
          location: null,
          hasTime: true,
          isAllDay: false,
          isDuplicate: false,
          isRecurring: false,
          classID: classID,
          created: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          status: 'pending', // This will be a pending event
          source: 'paste',
          originalDateText: 'in 2 days',
          timeText: '4pm',
          participant: null,
          isClassEvent: true // Mark as class event
        }
      ];

      const arbor1PendingRef = collection(db, 'Arbor1PendingEvents');
      const addPromises = preloadedPendingEvents.map(event => addDoc(arbor1PendingRef, event));
      
      await Promise.all(addPromises);
      console.log(`Added ${preloadedPendingEvents.length} shared pending events to Arbor1PendingEvents collection`);
    } catch (error) {
      console.error('Error adding preloaded pending events:', error);
    }
  }

  // Function to add preloaded TLDRs to Arbor1TLDRs collection
  async function addPreloadedTLDRsToArbor1(classID = 'ArborSchool-1ST(Grade One)') {
    try {
      const preloadedTLDR = {
        category: "announcement",
        createdAt: new Date("2025-05-22T14:49:09.000Z"),
        emailDate: new Date("2025-05-22T14:46:12.000Z"),
        emailFrom: "Homi Graham <testmyhomi@gmail.com>",
        emailId: "196f875b1a7c3fd6_arbor1",
        emailSubject: "Fwd: New Message from Charlie Shrimpton",
        isRead: false,
        priority: "high",
        summary: "**Week of 13 May Update (Year 1 & 2):** - Arbor's Got Talent competition returns for 2025, starting June 13. - Phase 1 involves house votes and auditions on June 13, selecting 2 winners per house. - Phase 2 will have year group auditions to find finalists, with finals on June 25. - Emphasis on fun and fair competition for all participants. - **Upcoming Events:** - 📅 *June 13*: Arbor's Got Talent Phase 1 – House auditions. - 📅 *June 25*: Arbor's Got Talent Finals. - 🎤 *By June 1*: Confirm participation and act details with your child's teacher. - 📚 Encourage children to practice their acts at home. - 📝 Remind children to bring any necessary equipment for their performances.",
        tags: ["Arbor's Got Talent", "Year 1", "Year 2", "competition", "events"],
        classID: classID,
        isTLDR: true,
        isClassTLDR: true, // Mark as class TLDR
        source: 'preloaded'
      };

      // Store in Arbor1TLDRs collection
      const arbor1TLDRsRef = collection(db, 'Arbor1TLDRs');
      const existingQuery = query(arbor1TLDRsRef, 
        where('emailId', '==', preloadedTLDR.emailId),
        where('classID', '==', classID)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (existingSnapshot.empty) {
        await addDoc(arbor1TLDRsRef, preloadedTLDR);
        console.log('Added preloaded TLDR to Arbor1TLDRs collection');
      } else {
        console.log('Preloaded TLDR already exists in Arbor1TLDRs');
      }
    } catch (error) {
      console.error('Error adding preloaded TLDR:', error);
    }
  }

  const value = {
    currentUser,
    user: currentUser, // Add alias for compatibility
    login,
    signup,
    logout,
    addPreloadedEventsToArbor1,
    addPreloadedPendingEventsToArbor1,
    addPreloadedTLDRsToArbor1
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}